/**
 * Pure derivation of the dashboard delta chips' basis: which current-side
 * figures the chips compare (the day-capped fetch for a month-to-date
 * selection, else the headline figures -- see periodState.ts's
 * computeComparisonContext) and whether the chips may render at all. Kept
 * out of DashboardPage so the gating rules are unit-testable on their own.
 */
import type { CurrencySummary, ReportsSummaryResponse, RevenueResult, RevenueTotalRow } from '../../api/index.ts';
import type { LoadState } from '../../hooks/useLoadState.ts';
import { pickCurrencySummary, pickRevenueTotal } from './dashboardHelpers.ts';
import type { ComparisonContext } from './periodState.ts';

export interface ChipBasisInput {
  comparisonContext: Pick<ComparisonContext, 'toDay' | 'comparable'>;
  showInternalDetail: boolean;
  currencyCode: string | null;
  currentSummary: ReportsSummaryResponse;
  currentState: LoadState;
  previousState: LoadState;
  cappedCurrentSummary: ReportsSummaryResponse;
  cappedCurrentState: LoadState;
  revenue: RevenueResult;
  revenueState: LoadState;
  previousRevenueState: LoadState;
  cappedCurrentRevenue: RevenueResult;
  cappedCurrentRevenueState: LoadState;
}

export interface ChipBasis {
  chipCurrentForCurrency: CurrencySummary;
  chipRevenueTotal: RevenueTotalRow;
  showDeltas: boolean;
  showRevenueDeltas: boolean;
}

export function deriveChipBasis(input: Readonly<ChipBasisInput>): ChipBasis {
  const { comparisonContext, currencyCode } = input;
  const code = currencyCode ?? '';
  const chipCapped = comparisonContext.toDay !== null;
  const chipSummary = chipCapped ? input.cappedCurrentSummary : input.currentSummary;
  const chipRevenue = chipCapped ? input.cappedCurrentRevenue : input.revenue;

  // Both chip sides must be settled from the SAME request round: a reload
  // keeps stale data, so one side resolving first would pair mismatched periods.
  const summarySidesReady =
    input.previousState.kind === 'ready' && (chipCapped ? input.cappedCurrentState : input.currentState).kind === 'ready';
  const revenueSidesReady =
    input.previousRevenueState.kind === 'ready' &&
    (chipCapped ? input.cappedCurrentRevenueState : input.revenueState).kind === 'ready';

  const isEmptyPeriod = input.currentState.kind === 'ready' && input.currentSummary.by_currency.length === 0;
  // No capped row yet = days 1..toDay not imported (import lag): nothing to
  // compare, not a -100% drop.
  const cappedHasNoData = chipCapped && !chipSummary.by_currency.some((c) => c.currency_code === code);

  const showDeltas =
    input.showInternalDetail && comparisonContext.comparable && !isEmptyPeriod && summarySidesReady && !cappedHasNoData;

  return {
    chipCurrentForCurrency: pickCurrencySummary(chipSummary.by_currency, code),
    chipRevenueTotal: pickRevenueTotal(chipRevenue.totals, code),
    showDeltas,
    showRevenueDeltas: showDeltas && revenueSidesReady,
  };
}
